import { useMutation, useQuery } from '@apollo/client';
import axios from 'axios';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { GENERATE_SIGNED_URL } from '../../../graph-queries/tar/media';
import { MEDIA_ALLOWED_MIME_TYPES } from '../../../lib/constants/dbConstants';
import { MediaContext, MediaMetadataMimeEnum } from '../../../graph-queries/__generated__/graphql';
import { useMediaUploadReducer } from '../../organisms/MediaUpload/MediaUpload.reducer';
import { CERTIFICATE_ATTACHMENTS } from '../../../graph-queries/tar/certificate';
import { useEffect, useState } from 'react';

const MAX_FILE_SIZE_ALLOWED = 100_000_000;

export const useFileUpload = (mediaContext: MediaContext) => {
  const { sendError } = useNoticeBar();
  const [state, dispatch] = useMediaUploadReducer();
  const [generateUrl] = useMutation(GENERATE_SIGNED_URL);

  const uploadFile = async (
    file: File,
    handleUploadSuccess: (
      generated: { blobLocation: string; url: string },
      file: File
    ) => Promise<unknown>
  ) => {
    if (file.size > MAX_FILE_SIZE_ALLOWED) {
      const error = Error('File size exceeds 100MB');
      dispatch({ type: 'fail', file, error });
      return Promise.reject(error);
    }

    const mime = Object.entries(MEDIA_ALLOWED_MIME_TYPES).find(([key, val]) => {
      if (val === file.type) return key;
    })?.[0] as MediaMetadataMimeEnum;
    if (!mime) {
      const error = Error('Invalid file type');
      dispatch({ type: 'fail', file, error });
      return Promise.reject(error);
    }

    return generateUrl({
      variables: { fileName: file.name, mime: file.type, mediaContext },
    })
      .then((res) => {
        if (!res.data) return Promise.reject('Upload URL not generated');

        const generated = res.data.generateSignedUrl;

        return axios
          .put(generated.url, file, {
            headers: { 'x-ms-blob-type': 'BlockBlob' },
            onUploadProgress: (progressEvent) => {
              dispatch({ type: 'progress', file, progress: progressEvent.progress || 0 });
            },
          })
          .then(() => generated);
      })
      .then((generated) => handleUploadSuccess(generated, file))
      .then(() => {
        dispatch({ type: 'complete', file });
      })
      .catch((error) => {
        console.error(error);
        dispatch({ type: 'fail', file, error });
        throw error;
      });
  };

  const retry = (
    file: File,
    handleUploadSuccess: (
      generated: { blobLocation: string; url: string },
      file: File
    ) => Promise<unknown>
  ) => {
    dispatch({ type: 'start', file });
    uploadFile(file, handleUploadSuccess).catch(() =>
      sendError('Upload failed. Remove invalid file to continue.')
    );
  };

  return { state, dispatch, uploadFile, retry };
};

const MAX_COUNT = 7;

export const useCertificateAttachments = (certificateId: number) => {
  const { data, loading: loadingAttachments } = useQuery(CERTIFICATE_ATTACHMENTS, {
    variables: { certificateId },
  });
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if ((data?.attachmentsByCertificateId.length || 0) <= MAX_COUNT) setShowAll(true);
  }, [data?.attachmentsByCertificateId]);

  const attachments = (data?.attachmentsByCertificateId || [])
    .filter((_, index) => showAll || index < MAX_COUNT)
    .map((att) => ({
      id: att.attachmentId,
      url: att.attachmentUrl,
      fileName: att.fileName,
      mime: att.mime,
    }));

  return { attachments, showAll, setShowAll, loadingAttachments };
};
